import { useEffect, useState } from "react";

import { Button } from "~/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog";
import { Input } from "~/components/ui/input";
import { Label } from "~/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "~/components/ui/select";
import { Switch } from "~/components/ui/switch";
import type { Rack, RackFormData, RackType } from "~/types/wms";
import { RACK_TYPE_LABELS } from "~/types/wms";

interface RackDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  rack?: Rack;
  hallwayId?: string;
  onSubmit: (data: RackFormData) => void;
  isLoading: boolean;
}

export function RackDialog({
  open,
  onOpenChange,
  rack,
  hallwayId,
  onSubmit,
  isLoading,
}: RackDialogProps) {
  const [formData, setFormData] = useState<RackFormData>({
    hallway_id: "",
    code: "",
    rack_type: "selective",
    sequence: 1,
    is_active: true,
  });

  useEffect(() => {
    if (rack) {
      setFormData({
        hallway_id: rack.hallway_id,
        code: rack.code,
        barcode: rack.barcode || undefined,
        rack_type: rack.rack_type,
        max_weight_kg: rack.max_weight_kg || undefined,
        sequence: rack.sequence,
        is_active: rack.is_active,
      });
    } else {
      setFormData({
        hallway_id: hallwayId || "",
        code: "",
        rack_type: "selective",
        sequence: 1,
        is_active: true,
      });
    }
  }, [rack, hallwayId, open]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(formData);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>{rack ? "Editar Rack" : "Nuevo Rack"}</DialogTitle>
          </DialogHeader>

          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="code" className="text-right">
                Código *
              </Label>
              <Input
                id="code"
                value={formData.code}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    code: e.target.value.toUpperCase(),
                  })
                }
                className="col-span-3"
                placeholder="R01"
                required
              />
            </div>

            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="barcode" className="text-right">
                Barcode
              </Label>
              <Input
                id="barcode"
                value={formData.barcode || ""}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    barcode: e.target.value || undefined,
                  })
                }
                className="col-span-3"
                placeholder="Auto-generado si vacío"
              />
            </div>

            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="rack_type" className="text-right">
                Tipo *
              </Label>
              <Select
                value={formData.rack_type}
                onValueChange={(value) =>
                  setFormData({ ...formData, rack_type: value as RackType })
                }
              >
                <SelectTrigger className="col-span-3">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(RACK_TYPE_LABELS).map(([value, label]) => (
                    <SelectItem key={value} value={value}>
                      {label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="max_weight_kg" className="text-right">
                Peso Máx (kg)
              </Label>
              <Input
                id="max_weight_kg"
                type="number"
                min={0}
                value={formData.max_weight_kg || ""}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    max_weight_kg: e.target.value
                      ? parseInt(e.target.value)
                      : undefined,
                  })
                }
                className="col-span-3"
                placeholder="1000"
              />
            </div>

            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="sequence" className="text-right">
                Secuencia
              </Label>
              <Input
                id="sequence"
                type="number"
                min={1}
                value={formData.sequence}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    sequence: parseInt(e.target.value) || 1,
                  })
                }
                className="col-span-3"
              />
            </div>

            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="is_active" className="text-right">
                Activo
              </Label>
              <div className="col-span-3">
                <Switch
                  id="is_active"
                  checked={formData.is_active}
                  onCheckedChange={(checked) =>
                    setFormData({ ...formData, is_active: checked })
                  }
                />
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              Cancelar
            </Button>
            <Button type="submit" disabled={isLoading}>
              {isLoading ? "Guardando..." : rack ? "Guardar" : "Crear"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
